/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1674086400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Thu Jan 19 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "attentive",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Getting started",
          "depth": 0,
          "id": "getting-started"
        }, {
          "value": "Syncing data",
          "depth": 0,
          "id": "syncing-data"
        }, {
          "value": "Identity",
          "depth": 1,
          "id": "identity"
        }, {
          "value": "Custom events",
          "depth": 1,
          "id": "custom-events"
        }, {
          "value": "Custom attributes",
          "depth": 1,
          "id": "custom-attributes"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      ul: "ul",
      li: "li",
      code: "code",
      h3: "h3",
      h4: "h4",
      img: "img"
    }, _provideComponents(), props.components), {EventSyncWarning, NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!EventSyncWarning) _missingMdxReference("EventSyncWarning", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "This destination lets you push data from various sources into Attentive to maintain an up-to-date subscriber list for mobile messaging campaigns. Add attributes to subscribers in Attentive to build better segments for\npersonalized experiences and create user-associated events to trigger targeted workflows."
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Event Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Identity"
              })
            }), _jsx(_components.td, {
              children: "Associate user IDs with other identifiers"
            }), _jsx(_components.td, {
              children: "Update"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Custom Events"
              })
            }), _jsx(_components.td, {
              children: "Send any event-based data associated with a user"
            }), _jsx(_components.td, {
              children: "Insert"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Custom Attributes"
              })
            }), _jsx(_components.td, {
              children: "Add customizable data or characteristics to a user"
            }), _jsx(_components.td, {
              children: "Upsert"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "getting-started",
        children: "Getting started"
      }), "\n", _jsxs(_components.p, {
        children: ["To use the Attentive destination in Hightouch, you will need to generate an API key within Attentive.\nTo generate the API key, follow these ", _jsx(_components.a, {
          href: "https://docs.attentive.com/pages/authentication/",
          children: "steps"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "Ensure that the generated key has the correct scopes for the endpoints you wish to use:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Identity:"
          }), " ", _jsx(_components.code, {
            children: "identity:write"
          })]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Custom Events:"
          }), " ", _jsx(_components.code, {
            children: "events:write"
          })]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Custom Attributes:"
          }), " ", _jsx(_components.code, {
            children: "attributes:write"
          })]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "syncing-data",
        children: "Syncing data"
      }), "\n", _jsx(_components.h3, {
        id: "identity",
        children: "Identity"
      }), "\n", _jsxs(_components.p, {
        children: ["Syncing to Attentive identities lets you associate a client user identifier or custom identifier with other identifiers. A client user or custom identifier must be accompanied by at least one of the following identifiers:\n", _jsx(_components.code, {
          children: "phone"
        }), ", ", _jsx(_components.code, {
          children: "email"
        }), ", ", _jsx(_components.code, {
          children: "Shopify ID"
        }), ", ", _jsx(_components.code, {
          children: "Klaviyo ID"
        }), ", ", _jsx(_components.code, {
          children: "client user ID"
        }), ", or ", _jsx(_components.code, {
          children: "custom identifier"
        }), ".\nRefer to the ", _jsx(_components.a, {
          href: "https://docs.attentive.com/openapi/reference/tag/Identity/",
          children: "Attentive Identity API docs"
        }), " to learn more."]
      }), "\n", _jsx(_components.h4, {
        id: "matching-users",
        children: "Matching users"
      }), "\n", _jsx(_components.p, {
        children: "You can match rows in your model with users in Attentive on the following fields:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Email"
        }), "\n", _jsx(_components.li, {
          children: "Phone number"
        }), "\n", _jsx(_components.li, {
          children: "Client User ID"
        }), "\n", _jsx(_components.li, {
          children: "Shopify ID"
        }), "\n", _jsx(_components.li, {
          children: "Klaviyo ID"
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can map other identifiers to associate with the primary identifier, including external IDs not natively supported by Attentive."
      }), "\n", _jsx(_components.h3, {
        id: "custom-events",
        children: "Custom events"
      }), "\n", _jsx(_components.p, {
        children: "Syncing custom events lets you send user actions to use in the Attentive Segment Builder and the Journey Builder.\nThis data can't contain any sensitive or special categories of information as defined in applicable data\nprotection and privacy laws, including the California Consumer Privacy Act (CCPA) and California Privacy Rights Act (CPRA)."
      }), "\n", _jsxs(_components.p, {
        children: ["Refer to the ", _jsx(_components.a, {
          href: "https://docs.attentive.com/openapi/reference/tag/Custom-Events/",
          children: "Attentive Custom Events API docs"
        }), " to learn more."]
      }), "\n", _jsx(EventSyncWarning, {}), "\n", _jsx(_components.h4, {
        id: "associating-users",
        children: "Associating users"
      }), "\n", _jsx(_components.p, {
        children: "You can use the following fields to map associated events with users:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Email"
        }), "\n", _jsx(_components.li, {
          children: "Phone number"
        }), "\n", _jsx(_components.li, {
          children: "Client User ID"
        }), "\n", _jsx(_components.li, {
          children: "Shopify ID"
        }), "\n", _jsx(_components.li, {
          children: "Klaviyo ID"
        }), "\n", _jsx(_components.li, {
          children: "Custom Identifiers"
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "event-types",
        children: "Event types"
      }), "\n", _jsx(_components.p, {
        children: "The event type can be a static value or a field from the model to send multiple different event types.\nThe event type name is case-sensitive. For example, \"Order shipped\" and \"Order Shipped\" are different event types."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-attentive-event-type.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h4, {
        id: "event-universal-unique-identifier",
        children: "Event universal unique identifier"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch allows you to send an event universal unique identifier (UUID) for each event. Though optional, using a UUID is ", _jsx(_components.a, {
          href: "https://docs.attentive.com/openapi/reference/tag/Custom-Events/#tag/Custom-Events/operation/postCustomEvents",
          children: "recommended by Attentive"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-attentive-event-uuid.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h4, {
        id: "timestamps",
        children: "Timestamps"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch allows you to send a timestamp of when a custom event occurred in ISO 8601 format.\nIf the timestamp is older than 12 hours, relevant Journeys aren't triggered in Attentive.\nIf an event doesn't include a timestamp, the time of the API request is used."
      }), "\n", _jsx(_components.h4, {
        id: "event-properties",
        children: "Event properties"
      }), "\n", _jsx(_components.p, {
        children: "You can use properties to send metadata associated with the event."
      }), "\n", _jsx(_components.p, {
        children: "Object keys are expected to be strings and can't contain any of the following special characters:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Double quote (", _jsx(_components.code, {
            children: "\""
          }), ")"]
        }), "\n", _jsxs(_components.li, {
          children: ["Curly braces (", _jsx(_components.code, {
            children: "{ }"
          }), " )"]
        }), "\n", _jsxs(_components.li, {
          children: ["Square brackets (", _jsx(_components.code, {
            children: "[ ]"
          }), ")"]
        }), "\n", _jsxs(_components.li, {
          children: ["Backslash (", _jsx(_components.code, {
            children: "\\"
          }), ")"]
        }), "\n", _jsxs(_components.li, {
          children: ["Vertical bar (", _jsx(_components.code, {
            children: "|"
          }), ")"]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "custom-attributes",
        children: "Custom attributes"
      }), "\n", _jsxs(_components.p, {
        children: ["Syncing custom attributes lets you send attribute-based user data to Attentive. You can create as many custom attributes as needed. Note that you can create net-new properties with this API, however,\nit can't be used to create new values for an existing UI-created property name. Whenever you create a property name on the Attentive platform, you must also define all possible property values there. Refer to the ", _jsx(_components.a, {
          href: "https://docs.attentive.com/openapi/reference/tag/Custom-Attributes/",
          children: "Attentive Attributes API docs"
        }), " to learn more."]
      }), "\n", _jsx(_components.h4, {
        id: "associating-users-1",
        children: "Associating users"
      }), "\n", _jsx(_components.p, {
        children: "You can associate custom attributes with users on the following fields:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Email"
        }), "\n", _jsx(_components.li, {
          children: "Phone number"
        }), "\n", _jsx(_components.li, {
          children: "Client User ID"
        }), "\n", _jsx(_components.li, {
          children: "Shopify ID"
        }), "\n", _jsx(_components.li, {
          children: "Klaviyo ID"
        }), "\n", _jsx(_components.li, {
          children: "Custom Identifiers"
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "user-attributes",
        children: "User attributes"
      }), "\n", _jsx(_components.p, {
        children: "User attributes are any metadata associated with a user. Attribute object keys should be strings, and object values can be any type. Both object keys and object values are case-sensitive. \"Favorite color\" and \"Favorite Color\" are considered different custom attributes."
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
